import { computed, effect, Injectable, signal, untracked } from '@angular/core';

import { AuthService } from 'src/app/auth/services/auth.service';
import { INavFrame, INavStackTree, navFrame_create } from '../models/nav-frame';
import { IPageData, PageData } from 'src/app/infrastructure/services/page-services/models/page-data';
import { IRouteKeyData } from 'src/app/infrastructure/services/route-service/models/route-key-data';
import { ITabPageStorage, TabPageStorage } from '../../storage/models/tab-page-storage';
import { pageParent } from 'src/app/infrastructure/services/route-service/models/page-parent';
import { pageParentTab } from 'src/app/infrastructure/services/route-service/models/page-parent-tab';
import { parseJwt } from '../../storage/functions/parse-jwt';
import { routeName } from 'src/app/infrastructure/services/route-service/models/route-name';
import { routePage, TRoutePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { SignalTree } from '../../storage/types/signal-tree.type';
import { storageSignalTree } from '../../storage/functions/storage-signal-tree.function';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

const STACK_KEY = 'nav-stack';
const STACK_MAX = 15;
const SCRATCH_ID = '__scratch';
// Pagina's die onder een andere pagina kunnen hangen dan hun statische ouder (pageParent): een taak in een taak, een
// melding in een taak (fase 3c). Hun echte ouder is pas na het laden bekend: enter() pusht zo'n pagina boven een frame
// van een mogelijke ouder gewoon (zonder af te kappen tot de statische voorouder), daarna zet parent_ensure() de juiste
// ouder eronder.
const DYNAMIC_PARENTS: Partial<Record<TRoutePage, TRoutePage[]>> = {
    [routePage.task]: [routePage.task],
    [routePage.notice]: [routePage.task],
};

// De navigatiestack: een keten van bezochte pagina's waarvan de bovenste het heden is. Vervangt de
// oude combinatie van een per component gehardcodeerde terugkeerpagina en een losse returnPage-
// query-param, die elkaar tegenwerkten.
//
// Leeft in sessionStorage, dus per browsertab: overleeft F5, verdwijnt bij het sluiten van de tab, en
// twee tabbladen naast elkaar hebben elk hun eigen keten. Bewust niet gesynct naar de backend — een
// navigatiestack hoort niet mee te reizen naar een ander apparaat.
@Injectable({
    providedIn: 'root'
})
export class NavigationStackService {
    private readonly __authService = injectRequired(AuthService);

    private __tree$$ = storageSignalTree<INavStackTree>(
        () => STACK_KEY,
        { order: [], frames: {}, sequence: 0 },
        {
            type: 'session',
            debounce: 300
        }
    );

    private __userGuid: string | null | undefined = undefined;
    private __tabPending: { routePath: string, tabName: INavFrame['tabName'] } | null = null;

    private __order$$ = computed<string[]>(() => this.__tree$$()?.order ?? []);
    private __frames$$ = computed<Record<string, INavFrame>>(() => this.__tree$$()?.frames ?? {});

    private __idCurrent$$ = computed<string | null>(() => this.__order$$().at(-1) ?? null);

    frameCurrent$$ = computed<INavFrame | null>(() => {
        const __id = this.__idCurrent$$();
        return __id ? this.__frames$$()[__id] ?? null : null;
    });

    // De pagina ónder de huidige: bestemming van de terugknop. Geen frame eronder ⇒ null, waarna
    // _PageService.pageReturn_goto terugvalt op de startpagina.
    framePrevious$$ = computed<IPageData | null>(() => {
        const __order = this.__order$$();
        const __frame = __order.length > 1 ? this.__frames$$()[__order[__order.length - 2]] : null;

        return __frame ? this.__pageData_fromFrame(__frame) : null;
    });

    hasFrame$$ = computed<boolean>(() => this.__idCurrent$$() != null);

    // Tabkeuze voor de pagina die al open staat (zie tabName_preset): de navigatie ernaartoe is dan
    // een no-op, dus de pagina moet de tab zelf tonen — ook als de tabs verborgen zijn of de pagina
    // in Edit staat. `seq` maakt elke aanvraag uniek, zodat dezelfde tab nogmaals vragen ook telt.
    private __tabRequest$$ = signal<{ frameId: string, tabName: INavFrame['tabName'], seq: number } | null>(null);
    tabRequest$$ = this.__tabRequest$$.asReadonly();

    constructor() {
        // Account-wissel in dezelfde tab (uitloggen → inloggen als iemand anders, geen page-reload)
        // moet niet met andermans keten verderlopen. De eerste keer dat de guid bekend wordt is geen
        // wissel maar gewone hydratie — dan blijft de stack staan, zodat F5 de keten behoudt.
        effect(() => {
            const __token = this.__authService.token$$();
            const __guid = __token ? parseJwt(__token)?.nameid ?? null : null;

            untracked(() => {
                if (this.__userGuid === __guid) return;

                const __isSwitch = this.__userGuid !== undefined;
                this.__userGuid = __guid;

                if (__isSwitch) this.clear();
            });
        });
    }

    // Enige aanhechtingspunt op de route-lifecycle: aangeroepen vanuit _BaseRouteComponent._ngOnInit
    // bij elke paginamontage én bij elke param-wisseling.
    enter(page: TRoutePage, routeKeys: IRouteKeyData | null, url: string): void {
        const __path = this.__path_clean(url);
        if (!__path) return;

        const __tree = this.__tree$$();
        const __order = [...(__tree?.order ?? [])];
        const __frames = { ...(__tree?.frames ?? {}) };
        let __sequence = __tree?.sequence ?? 0;

        // 1. Exacte treffer — we komen terug op een pagina die al in de keten staat (terugknop,
        //    browser-back, of een drawer-klik naar een voorouder). Kap alles erboven af; de state
        //    van dit frame blijft staan en wordt daarmee hersteld.
        const __hit = __order.findIndex(id => __frames[id]?.routePath === __path);
        if (__hit >= 0) {
            this.__truncate(__order, __frames, __hit + 1);
            this.__tree$$.set({ order: __order, frames: __frames, sequence: __sequence });
            return;
        }

        const __top = __order.length ? __frames[__order[__order.length - 1]] : null;
        const __nested = !!__top?.routePage && (DYNAMIC_PARENTS[page]?.includes(__top.routePage) ?? false);

        const __ancestors = this.__ancestors(page, routeKeys);
        // Heeft deze pagina een échte hiërarchie, of hangt hij alleen onder Home? Image is het
        // typische tweede geval: die hoort nergens onder en moet dus zijn aanroeper bewaren.
        const __hierarchical = __ancestors.some(a => a.routePage !== routePage.home);

        let __base = __order.length;
        let __missing: IPageData[] = [];

        if (__order.length === 0) {
            // 2. Lege keten (deep link, F5 op een verse tab): de hele voorouderketen synthetiseren.
            __base = 0;
            __missing = __ancestors;
        } else if (__hierarchical && !__nested) {
            // 3. Kap af tot het diepste frame dat een voorouder van het doel is, en vul daarna de
            //    ontbrekende voorouders aan. Een zijsprong naar een ander domein (Project BA →
            //    Project AA) valt zo terug op de gedeelde wortel en krijgt Domain A eronder — niet
            //    het Domain B waar hij toevallig vandaan kwam.
            const __ancestorPaths = __ancestors.map(a => a.routePath);
            let __matched = -1;

            for (let i = __order.length - 1; i >= 0; i--) {
                if (__ancestorPaths.includes(__frames[__order[i]]?.routePath ?? null)) {
                    __matched = i;
                    break;
                }
            }

            if (__matched >= 0) {
                const __matchedPath = __frames[__order[__matched]].routePath;

                __base = __matched + 1;
                __missing = __ancestors.slice(__ancestorPaths.indexOf(__matchedPath) + 1);
            } else {
                __base = 0;
                __missing = __ancestors;
            }
        }
        // 4. Niet-hiërarchische pagina op een gevulde keten: gewoon pushen, zodat de aanroeper
        //    bewaard blijft (Notice → Image → terug naar diezelfde Notice).

        this.__truncate(__order, __frames, __base);

        for (const __ancestor of __missing) {
            __sequence++;
            const __ancestorId = `f${__sequence}`;

            __frames[__ancestorId] = navFrame_create(__ancestorId, __ancestor, __ancestor.routePath!);
            __order.push(__ancestorId);
        }

        // De direct gesynthetiseerde ouder (net aangemaakt, dus zonder eigen tab-historie) weet nog
        // niet welke tab bij dit kind hoort — zonder deze koppeling toont hij straks gewoon zijn
        // eerste tab. Alleen relevant als die ouder ook echt NIEUW is (__missing niet leeg); een
        // reeds bestaand frame draagt al zijn eigen, echte tabkeuze en die blijft onaangeroerd.
        const __parentTab = pageParentTab[page];
        if (__parentTab && __missing.length > 0) {
            const __parentId = __order[__order.length - 1];
            __frames[__parentId].tabName = __parentTab;
        }

        __sequence++;
        const __id = `f${__sequence}`;
        const __pageData = PageData.create(page, routeKeys);
        const __frame = navFrame_create(__id, __pageData, __path);

        // Tabkeuze die de drawer vlak vóór deze navigatie klaarzette (zie tabName_preset).
        if (this.__tabPending?.routePath === __path) __frame.tabName = this.__tabPending.tabName;
        this.__tabPending = null;

        __frames[__id] = __frame;
        __order.push(__id);

        while (__order.length > STACK_MAX) {
            const __dropped = __order.shift();
            if (__dropped) delete __frames[__dropped];
        }

        this.__tree$$.set({ order: __order, frames: __frames, sequence: __sequence });
    }

    // Dynamische ouder van de huidige pagina (DYNAMIC_PARENTS), aangeroepen door de pagina zodra zijn data er is. parent =
    // de dynamische ouder, of null als de pagina direct onder zijn statische voorouder hangt (hoofdtaak of
    // projectmelding onder Project). Frames van een mogelijke ouder direct onder de huidige die niet de ouder zijn, gaan
    // eruit; de ouder komt ertussen als hij er nog niet stond (met tabName als tab, bv. de meldingen van de taak). Bij
    // terug naar de ouder doet die hetzelfde voor zijn eigen ouder.
    parent_ensure(parent: IPageData | null, tabName?: INavFrame['tabName']): void {
        const __tree = this.__tree$$();
        const __order = [...(__tree?.order ?? [])];
        const __frames = { ...(__tree?.frames ?? {}) };
        let __sequence = __tree?.sequence ?? 0;
        if (__order.length === 0) return;

        const __currentId = __order[__order.length - 1];
        const __page = __frames[__currentId]?.routePage ?? null;
        const __parentPath = parent?.routePath ? this.__path_clean(parent.routePath) : null;
        const __below = __order.length > 1 ? __frames[__order[__order.length - 2]] : null;

        const __dynamic = __page ? DYNAMIC_PARENTS[__page] ?? [] : [];
        const __isDynamic = (id: string) => __dynamic.includes(__frames[id]?.routePage as TRoutePage);
        if (__parentPath ? __below?.routePath === __parentPath : !__below || !__isDynamic(__order[__order.length - 2])) return;

        let __start = __order.length - 2;
        while (__start >= 0 && __isDynamic(__order[__start])) __start--;
        for (const __id of __order.splice(__start + 1, __order.length - 1 - (__start + 1))) {
            delete __frames[__id];
        }

        if (parent && __parentPath) {
            __sequence++;
            const __parentId = `f${__sequence}`;
            __frames[__parentId] = navFrame_create(__parentId, parent, __parentPath);
            if (tabName) __frames[__parentId].tabName = tabName;
            __order.splice(__order.length - 1, 0, __parentId);
        }

        this.__tree$$.set({ order: __order, frames: __frames, sequence: __sequence });
    }

    clear(): void {
        this.__tree$$.set({ order: [], frames: {}, sequence: 0 });
    }

    // Node van het huidige frame, in de vorm die de page-/tab-storage-services verwachten. Bewust
    // een methode en geen vastgelegde property: de storage-services worden per pagina geïnstantieerd
    // vóórdat enter() het bijbehorende frame heeft gepusht, dus het pad moet per toegang opnieuw
    // worden bepaald.
    frameNode(): SignalTree<ITabPageStorage> {
        const __id = this.__idCurrent$$() ?? SCRATCH_ID;

        return this.__tree$$.frames[__id].ensure(() => new TabPageStorage() as INavFrame) as unknown as SignalTree<ITabPageStorage>;
    }

    // Kiest de tab van een pagina vooruit, vlak vóór het navigeren ernaartoe (de
    // navigation-history-drawer doet dit). Staat die pagina al op de stack — ook als het de huidige
    // is, want navigeren naar dezelfde url is een no-op — dan gaat de keuze meteen naar dat frame.
    // Anders wordt hij vastgehouden tot enter() het frame aanmaakt.
    tabName_preset(routePath: string, tabName: INavFrame['tabName']): void {
        const __path = this.__path_clean(routePath);
        const __frames = this.__frames$$();
        const __id = Object.keys(__frames).find(id => __frames[id]?.routePath === __path);

        if (__id) {
            this.__tree$$.frames[__id].tabName.set(tabName);
            if (__id === this.__idCurrent$$()) {
                this.__tabRequest$$.set({ frameId: __id, tabName, seq: (this.__tabRequest$$()?.seq ?? 0) + 1 });
            }
            return;
        }

        this.__tabPending = { routePath: __path, tabName };
    }

    // Voorouderketen van een pagina, wortel → directe ouder. Loopt de pageParent-map af en bouwt per
    // stap de url met de route-keys van het huidige bezoek (dauGuid/pamGuid), zodat de keten naar de
    // júiste domain/project-instantie wijst.
    private __ancestors(page: TRoutePage, routeKeys: IRouteKeyData | null): IPageData[] {
        const __chain: IPageData[] = [];
        let __parent: TRoutePage | null = pageParent[page] ?? null;
        let __guard = 0;

        while (__parent && __guard++ < 10) {
            const __pageData = PageData.create(__parent, routeKeys);

            if (__pageData?.routePath) __chain.unshift(__pageData);
            __parent = pageParent[__parent] ?? null;
        }

        return __chain;
    }

    private __pageData_fromFrame(frame: INavFrame): IPageData {
        return {
            routePage: frame.routePage,
            displayName: frame.displayName,
            routePath: frame.routePath,
            routeKeyData: frame.routeKeys,
        };
    }

    // Normaliseert een url naar de canonieke paginavorm die PageData ook oplevert: zonder query/hash
    // en zonder het afsluitende /details-segment. De router stuurt `:key` door naar `:key/details`,
    // dus router.url draagt dat segment wél en de uit PageData gebouwde voorouderpaden niet — zonder
    // deze normalisatie zou geen enkele exacte- of voorouder-treffer ooit matchen.
    private __path_clean(url: string): string {
        const __path = (url ?? '').split('?')[0].split('#')[0];

        return __path.replace(new RegExp(`/${routeName.details}$`), '');
    }

    private __truncate(order: string[], frames: Record<string, INavFrame>, length: number): void {
        for (const __id of order.splice(length)) {
            delete frames[__id];
        }
    }
}
